import React, { useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import Chevron from 'core-system/Icons/Actions/Chevron'
import LocationIcon from 'core-system/Icons/Misc/Location'
import { DashboardLocationCard } from '../dashboardTypes'
import LocationCard from './LocationCard'
import {
  LocationCountrySection,
  LocationGroupBy,
  groupLocationCountries,
} from './groupLocationCountries'

const COUNTRY_ACCENT = palette.accent.teal500

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Section = styled.div`
  background: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  overflow: hidden;
`

const Header = styled.button`
  appearance: none;
  width: 100%;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: 2px solid ${palette.primary.pink500};
    outline-offset: -2px;
  }
`

const ChevronWrap = styled.span<{ $open: boolean }>`
  display: flex;
  color: ${palette.text.secondary};
  transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const IconBadge = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.6rem;
  background: ${COUNTRY_ACCENT}1A;
  color: ${COUNTRY_ACCENT};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const TitleBlock = styled.div`
  min-width: 0;
  flex: 1;
`

const Metrics = styled.div`
  display: flex;
  align-items: center;
  gap: 1.75rem;
  margin-left: auto;
  flex-shrink: 0;
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 4.5rem;
`

const Panel = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(props) => (props.$open ? '1fr' : '0fr')};
  opacity: ${(props) => (props.$open ? 1 : 0)};
  pointer-events: ${(props) => (props.$open ? 'auto' : 'none')};
  transition:
    grid-template-rows 0.32s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.24s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const PanelInner = styled.div`
  min-height: 0;
  overflow: hidden;
`

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  padding: 0 1.25rem 1.25rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

interface LocationCountrySectionsProps {
  cards: DashboardLocationCard[]
  groupBy?: LocationGroupBy
  onCardClick: (card: DashboardLocationCard) => void
}

const formatCount = (value: number) => value.toLocaleString('en-US')

const LocationCountrySections = ({
  cards,
  groupBy = 'country',
  onCardClick,
}: LocationCountrySectionsProps) => {
  const sections = groupLocationCountries(cards, groupBy)
  const [openCountries, setOpenCountries] = useState<string[]>([])

  const toggle = (country: string) => {
    setOpenCountries((current) =>
      current.includes(country)
        ? current.filter((item) => item !== country)
        : [...current, country]
    )
  }

  return (
    <Stack data-cy='location-cards-row'>
      {sections.map((section) => (
        <CountrySection
          key={section.country}
          section={section}
          open={openCountries.includes(section.country)}
          onToggle={() => toggle(section.country)}
          onCardClick={onCardClick}
        />
      ))}
    </Stack>
  )
}

const CountrySection = ({
  section,
  open,
  onToggle,
  onCardClick,
}: {
  section: LocationCountrySection
  open: boolean
  onToggle: () => void
  onCardClick: (card: DashboardLocationCard) => void
}) => (
  <Section data-cy={`location-country-${section.country}`}>
    <Header type='button' aria-expanded={open} onClick={onToggle}>
      <ChevronWrap $open={open}>
        <Chevron width={18} height={18} />
      </ChevronWrap>
      <IconBadge>
        <LocationIcon width={18} height={18} color={COUNTRY_ACCENT} />
      </IconBadge>
      <TitleBlock>
        <Text variant='h4' fontWeight={600}>
          {section.title}
        </Text>
        {section.description ? (
          <Text variant='body1' color={palette.text.secondary}>
            {section.description}
          </Text>
        ) : null}
      </TitleBlock>
      <Metrics>
        <Stat>
          <Text variant='h4' fontWeight={600}>
            {section.worksites}
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            Worksites
          </Text>
        </Stat>
        <Stat>
          <Text variant='h4' fontWeight={600}>
            {formatCount(section.employees)}
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            Employees
          </Text>
        </Stat>
        <Stat>
          <Text variant='h4' fontWeight={600}>
            {formatCount(section.enrolled)}
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            Enrolled
          </Text>
        </Stat>
        <Stat>
          <Text variant='h4' fontWeight={600}>
            {section.cities}
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            Cities
          </Text>
        </Stat>
      </Metrics>
    </Header>
    <Panel $open={open} aria-hidden={!open} inert={!open}>
      <PanelInner>
        <Cards>
          {section.cards.map((card) => (
            <LocationCard
              key={card.locationKey}
              card={card}
              onClick={onCardClick}
            />
          ))}
        </Cards>
      </PanelInner>
    </Panel>
  </Section>
)

export default React.memo(LocationCountrySections)
